import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";

const UIFeedbackContext = createContext(null);

export function UIFeedbackProvider({ children }) {
  const [confirmState, setConfirmState] = useState(null);
  // { message, title, confirmLabel, danger, resolve }
  const [toasts, setToasts] = useState([]); // [{ id, message, type }]
  const toastId = useRef(0);

  const confirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        message,
        title: opts.title || "Are you sure?",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        danger: !!opts.danger,
        resolve,
      });
    });
  }, []);

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  const showToast = useCallback((message, type = "info", duration = 3200) => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  return (
    <UIFeedbackContext.Provider value={{ confirm, showToast }}>
      {children}

      {confirmState && ReactDOM.createPortal(
        <div
          onClick={(e) => { if (e.target === e.currentTarget) closeConfirm(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
        >
          <div style={{
            width: "420px", maxWidth: "92vw", background: "#0d1424",
            border: `1px solid ${confirmState.danger ? "rgba(255,102,102,0.4)" : "rgba(0,225,255,0.35)"}`,
            borderRadius: "12px", padding: "22px",
          }}>
            <div style={{
              fontSize: "16px", fontWeight: 600,
              color: confirmState.danger ? "#ff6666" : "#00e1ff", marginBottom: "10px",
            }}>
              {confirmState.title}
            </div>
            <p style={{ color: "#ccc", fontSize: "14px", lineHeight: 1.5, margin: "0 0 20px 0" }}>
              {confirmState.message}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => closeConfirm(false)}
                style={{
                  background: "none", border: "1px solid rgba(255,255,255,0.2)", color: "#ccc",
                  borderRadius: "6px", padding: "8px 16px", cursor: "pointer",
                }}
              >
                {confirmState.cancelLabel}
              </button>
              <button
                onClick={() => closeConfirm(true)}
                style={{
                  background: confirmState.danger ? "#ff4d4d" : "#028be8", color: "white",
                  border: "none", borderRadius: "6px", padding: "8px 18px", fontWeight: 600, cursor: "pointer",
                }}
              >
                {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {toasts.length > 0 && ReactDOM.createPortal(
        <div style={{
          position: "fixed", bottom: "90px", left: "50%", transform: "translateX(-50%)",
          zIndex: 9500, display: "flex", flexDirection: "column", gap: "8px", alignItems: "center",
        }}>
          {toasts.map((t) => (
            <div key={t.id} style={{
              background: t.type === "error" ? "rgba(255,77,77,0.95)"
                : t.type === "success" ? "rgba(0,200,117,0.95)"
                : "rgba(2,139,232,0.95)",
              color: "white", padding: "10px 18px", borderRadius: "8px",
              fontSize: "13.5px", fontWeight: 500, boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
              maxWidth: "90vw",
            }}>
              {t.message}
            </div>
          ))}
        </div>,
        document.body
      )}
    </UIFeedbackContext.Provider>
  );
}

export function useUIFeedback() {
  const ctx = useContext(UIFeedbackContext);
  if (!ctx) throw new Error("useUIFeedback must be used within UIFeedbackProvider");
  return ctx;
}